/**
 * ShipToSearch — typeahead search dropdown for selecting a ship-to address.
 *
 * When customerNo is set, searches only that customer's addresses by default.
 * Provides a "Search all customers" toggle to search across all customers,
 * with mismatch warnings when the selected address belongs to a different customer.
 *
 * Exports ShipToAddress (legacy type) for backward compatibility with
 * consumers that reference it.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { portalService } from '../../services/portal';
import { useClickOutside } from '../../hooks/useClickOutside';
import styles from './ShipToSearch.module.css';

// ---------------------------------------------------------------------------
// Result type returned to consumers on selection
// ---------------------------------------------------------------------------

export interface ShipToResult {
  ship_to_code: string;
  ship_to_name: string;
  address1: string;
  address2?: string;
  address3?: string;
  city: string;
  state: string;
  zip_code: string;
  warehouse_code?: string;
  customer_no?: string;
  customer_name?: string;
  is_billing_fallback?: boolean;
  /**
   * True when the row is ChemMasters' OWN Madison OH dock (will-call
   * pickup), not a customer location. Selecting it requires confirmation.
   */
  is_chemmasters_facility?: boolean;
}

// Legacy type kept for backward compat with CreateOrderDialog
export interface ShipToAddress {
  id: string;
  ship_to_code: string | null;
  ship_to_name: string | null;
  ship_to_address1: string | null;
  ship_to_address2: string | null;
  ship_to_address3: string | null;
  ship_to_city: string | null;
  ship_to_state: string | null;
  ship_to_zip_code: string | null;
  warehouse_code: string | null;
  salesperson_no: string | null;
}

// ---------------------------------------------------------------------------
// Raw API result shapes (mirror the backend response models)
// ---------------------------------------------------------------------------

interface RawShipToResult {
  id: string;
  ship_to_code: string | null;
  ship_to_name: string | null;
  ship_to_address1: string | null;
  ship_to_address2: string | null;
  ship_to_address3?: string | null;
  ship_to_city: string | null;
  ship_to_state: string | null;
  ship_to_zip_code: string | null;
  warehouse_code: string | null;
  salesperson_no: string | null;
  customer_no?: string | null;
  customer_name?: string | null;
  is_chemmasters_facility?: boolean;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ShipToSearchProps {
  /** If set, search only this customer's addresses. If null, search all addresses. */
  customerNo: string | null;
  /** Current customer name for mismatch warning */
  customerName?: string;
  /** Called when a ship-to address is selected */
  onSelect: (address: ShipToResult) => void;
  /**
   * Cancel when the user clicks anywhere outside the search (default true,
   * the right behaviour for the inline pickers in the email sidebar and the
   * Sage editor). The order modal's docked ship-to panel passes false: there
   * the CSR clicks the email beside the order to read the address, and a
   * stray click must not throw the picker away (kanban 2c097936).
   */
  closeOnClickOutside?: boolean;
  /**
   * 'overlay' (default) floats the results under the input, over whatever is
   * below. 'inline' lays them out in the flow so a host that clips or docks
   * the search to the bottom of the viewport (the order modal's ship-to
   * panel) grows to show them instead of cutting them off.
   */
  dropdownPlacement?: 'overlay' | 'inline';
  /** Called when ship-to selection would change the customer */
  onCustomerMismatch?: (
    address: ShipToResult,
    ownerCustomerNo: string,
    ownerCustomerName: string,
  ) => void;
  /** Called on cancel/close */
  onCancel: () => void;
  /**
   * When provided, renders an "Address not listed? Create a new Sage ship-to"
   * entry point. It lives INSIDE the dropdown footer while the dropdown is
   * open — the dropdown is absolutely positioned and paints over anything the
   * parent renders below this component, so an external button would be
   * covered and unclickable.
   */
  onCreateNew?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DEBOUNCE_MS = 300;

function toShipToResult(raw: RawShipToResult, includeCustomer: boolean): ShipToResult {
  return {
    ship_to_code: raw.ship_to_code ?? '',
    ship_to_name: raw.ship_to_name ?? '',
    address1: raw.ship_to_address1 ?? '',
    address2: raw.ship_to_address2 ?? undefined,
    address3: raw.ship_to_address3 ?? undefined,
    city: raw.ship_to_city ?? '',
    state: raw.ship_to_state ?? '',
    zip_code: raw.ship_to_zip_code ?? '',
    warehouse_code: raw.warehouse_code ?? undefined,
    is_chemmasters_facility: raw.is_chemmasters_facility ?? false,
    ...(includeCustomer
      ? {
          customer_no: raw.customer_no ?? undefined,
          customer_name: raw.customer_name ?? undefined,
        }
      : {}),
  };
}

/** Same Sage row? (code + owner — the owner is only present in search-all mode) */
function sameRow(a: ShipToResult, b: ShipToResult): boolean {
  return a.ship_to_code === b.ship_to_code && (a.customer_no ?? '') === (b.customer_no ?? '');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ShipToSearch: React.FC<ShipToSearchProps> = ({
  customerNo,
  customerName,
  onSelect,
  onCustomerMismatch,
  onCancel,
  onCreateNew,
  closeOnClickOutside = true,
  dropdownPlacement = 'overlay',
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ShipToResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [searchAll, setSearchAll] = useState(customerNo === null);
  // A ChemMasters-facility row the user clicked but has not confirmed yet.
  // The dock is a legitimate will-call choice, so it stays selectable — but
  // never on a single click (AU00538: ARMST03's 002 keyed to a delivery).
  const [pendingFacility, setPendingFacility] = useState<ShipToResult | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus input on mount and load initial results for customer-scoped search
  useEffect(() => {
    inputRef.current?.focus();

    // Pre-load addresses when we have a customer and aren't searching all
    if (customerNo && !searchAll) {
      setLoading(true);
      portalService
        .searchShipTo(customerNo, '', 20)
        .then((raw) => {
          const addresses = raw.map((r) => toShipToResult(r, false));
          setResults(addresses);
          setSearched(true);
        })
        .catch(() => {
          // Silently fail — user can still type to search
        })
        .finally(() => setLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useClickOutside(wrapperRef, onCancel, closeOnClickOutside);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.trim().length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        let addresses: ShipToResult[];

        if (!searchAll && customerNo) {
          const raw = await portalService.searchShipTo(customerNo, query, 10);
          addresses = raw.map((r) => toShipToResult(r, false));
        } else {
          const raw = await portalService.searchAllShipTo(query, 10);
          addresses = raw.map((r) => toShipToResult(r, true));
        }

        setResults(addresses);
        setSearched(true);
        setHighlightIndex(-1);
        setPendingFacility(null);
      } catch {
        setError('Search failed');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, searchAll, customerNo]);

  const commitSelect = useCallback(
    (address: ShipToResult) => {
      setPendingFacility(null);
      // Check for customer mismatch when searching all and a specific customer is set
      if (
        searchAll &&
        customerNo &&
        address.customer_no &&
        address.customer_no !== customerNo &&
        onCustomerMismatch
      ) {
        onCustomerMismatch(
          address,
          address.customer_no,
          address.customer_name ?? 'Unknown',
        );
        return;
      }
      onSelect(address);
    },
    [searchAll, customerNo, onSelect, onCustomerMismatch],
  );

  const handleSelect = useCallback(
    (address: ShipToResult) => {
      // Our own dock needs an explicit second step before it is applied.
      if (address.is_chemmasters_facility) {
        setPendingFacility(address);
        return;
      }
      commitSelect(address);
    },
    [commitSelect],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      // Spent here: the host (order modal, split view) listening on window
      // must not also close on this press (kanban 2c097936).
      e.preventDefault();
      onCancel();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0 && results[highlightIndex]) {
      e.preventDefault();
      handleSelect(results[highlightIndex]);
    }
  };

  const toggleSearchAll = () => {
    setSearchAll((prev) => !prev);
    setResults([]);
    setSearched(false);
    setHighlightIndex(-1);
    setPendingFacility(null);
  };

  const showDropdown = (loading || searched) && (query.trim().length >= 2 || results.length > 0);

  /** Does this address belong to a different customer than the current one? */
  const isMismatch = (address: ShipToResult): boolean =>
    !!(searchAll && customerNo && address.customer_no && address.customer_no !== customerNo);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.inputRow}>
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          placeholder={searchAll ? 'Search all ship-to addresses...' : 'Search ship-to addresses...'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
        />
        <button
          type="button"
          className={styles.cancelBtn}
          onClick={onCancel}
          title="Cancel"
        >
          &times;
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {showDropdown && (
        <div
          className={dropdownPlacement === 'inline' ? `${styles.dropdown} ${styles.dropdownInline}` : styles.dropdown}
          role="listbox"
        >
          {searchAll && (
            <div className={styles.modeLabel}>Searching all customers</div>
          )}

          {loading && (
            <div className={styles.loading}>Searching...</div>
          )}
          {!loading && searched && results.length === 0 && (
            <div className={styles.emptyState}>
              No ship-to addresses found
              {query.trim().length > 0 && (
                <div className={styles.emptyHint}>
                  Try searching by code, name, city, state, or zip
                </div>
              )}
            </div>
          )}
          {!loading &&
            results.map((addr, i) => (
              <div key={`${addr.customer_no ?? 'x'}-${addr.ship_to_code}-${i}`}>
              <button
                type="button"
                className={`${styles.option} ${i === highlightIndex ? styles.optionHighlighted : ''} ${addr.is_chemmasters_facility ? styles.optionFacility : ''}`}
                onClick={() => handleSelect(addr)}
                role="option"
                aria-selected={i === highlightIndex}
                data-testid={addr.is_chemmasters_facility ? 'ship-to-option-facility' : 'ship-to-option'}
                data-action="ship_to_pick"
                data-target-ship-to-code={addr.ship_to_code}
                data-target-customer-no={addr.customer_no ?? undefined}
              >
                <div className={styles.optionMain}>
                  <span className={styles.optionName}>
                    {addr.ship_to_name || 'Unknown'}
                  </span>
                  <span className={styles.optionCode}>
                    {addr.ship_to_code}
                  </span>
                </div>
                <div className={styles.optionAddress}>
                  {addr.address1}
                  {addr.address2 && `, ${addr.address2}`}
                  {addr.address3 && `, ${addr.address3}`}
                  {addr.city && addr.state && `, ${addr.city}, ${addr.state}`}
                  {addr.zip_code && ` ${addr.zip_code}`}
                </div>
                <div className={styles.optionMeta}>
                  {addr.is_chemmasters_facility && (
                    <span className={styles.facilityBadge}>
                      ChemMasters facility
                    </span>
                  )}
                  {addr.warehouse_code && (
                    <span className={styles.whBadge}>
                      WH: {addr.warehouse_code}
                    </span>
                  )}
                  {searchAll && addr.customer_name && (
                    <span className={styles.customerBadge}>
                      {addr.customer_name} ({addr.customer_no})
                    </span>
                  )}
                </div>
                {addr.is_chemmasters_facility && (
                  <div className={styles.warning}>
                    This is ChemMasters&apos; own Madison OH dock (will-call pickup), not a
                    customer location
                  </div>
                )}
                {isMismatch(addr) && (
                  <div className={styles.warning}>
                    This address belongs to {addr.customer_name} ({addr.customer_no}), not{' '}
                    {customerName ?? customerNo}
                  </div>
                )}
              </button>
              {pendingFacility && sameRow(pendingFacility, addr) && (
                <div className={styles.confirmBar} role="alert" data-testid="facility-confirm">
                  <span className={styles.confirmText}>
                    Ship to ChemMasters&apos; own dock? Only pick this for a will-call /
                    customer-pickup order.
                  </span>
                  <span className={styles.confirmActions}>
                    <button
                      type="button"
                      className={styles.confirmUseBtn}
                      onClick={() => commitSelect(pendingFacility)}
                      data-testid="facility-confirm-use"
                    >
                      Yes, will-call pickup
                    </button>
                    <button
                      type="button"
                      className={styles.confirmCancelBtn}
                      onClick={() => setPendingFacility(null)}
                      data-testid="facility-confirm-cancel"
                    >
                      Choose another
                    </button>
                  </span>
                </div>
              )}
              </div>
            ))}

          {/* Toggle to search all customers */}
          {!loading && searched && customerNo && (
            <button
              type="button"
              className={styles.searchAllLink}
              onClick={toggleSearchAll}
            >
              {searchAll ? 'Search only current customer' : 'Search all customers'}
            </button>
          )}

          {onCreateNew && (
            <button
              type="button"
              className={styles.createNewLink}
              onClick={onCreateNew}
              data-testid="open-new-ship-to-form"
            >
              Address not listed? Create a new Sage ship-to
            </button>
          )}
        </div>
      )}

      {/* When the dropdown is closed the footer above doesn't exist — keep the
          create entry point reachable below the input. */}
      {!showDropdown && onCreateNew && (
        <button
          type="button"
          className={styles.createNewStandalone}
          onClick={onCreateNew}
          data-testid="open-new-ship-to-form"
        >
          Address not listed? Create a new Sage ship-to
        </button>
      )}
    </div>
  );
};

export default ShipToSearch;
